import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Checkbox,
    Typography, IconButton, Input
} from "@material-tailwind/react";
import { useEffect } from "react";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { XMarkIcon, PlusIcon, TrashIcon, MinusIcon } from "@heroicons/react/24/outline";
import Toaster from "@/components/Toaster";
import { fetchColorAttribute, fetchSizeAttribute } from "@/store/slices/productSlice";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import * as yup from "yup";
import { resetStatus, addRfq, fetchCustomer, fetchCartItem } from "@/store/slices/rfqSlice";
import ProductVariantSelector from "../../products/product-varients";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import DeliveryLocationSearch from "@/components/DeliveryLocationSearch";
import { useTranslation } from "react-i18next";
import { getCurrencies } from "@/store/slices/currentCountrySlice";

const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const schema = yup.object().shape({
    rfq_reference: yup.string().when("rfq_id", {
        is: (val) => !val,
        then: (schema) =>
            schema
                .required("rfq_ref_num_required")
                .matches(notBlankRegex, "field_not_empty")
                .matches(noHTMLRegex, "html_not_allowed")
                .matches(noCodeRegex, "code_not_allowed")
                .matches(noConsecutiveSpecialChars, "no_consecutive_special_chars")
                .matches(noLeadingTrailingPunct, "no_leading_trailing_punct")
                .min(3, "rfq_ref_min_chars")
                .max(15, "rfq_ref_max_chars"),
        otherwise: (schema) => schema.notRequired(),
    }),

    user_id: yup
        .string()
        .required("customer_required"),
    currency_code: yup
        .string()
        .trim()
        .uppercase()
        .length(3, "currency_code_length")
        .required("currency_code_required"),

    customer_delivery_address: yup.string().nullable().notRequired(),
    latitude: yup.number().nullable().notRequired(),
    longitude: yup.number().nullable().notRequired(),

    products: yup
        .array()
        .of(
            yup.object().shape({
                product_id: yup
                    .number()
                    .typeError("product_required")
                    .required("product_required"),

                quantity: yup
                    .number()
                    .typeError("quantity_required")
                    .required("quantity_required")
                    .min(1, "quantity_min")
                    .max(99999, "quantity_max"),

                attribute_value_color_id: yup
                    .number()
                    .typeError("color_required")
                    .required("color_required"),

                attribute_value_size_id: yup
                    .number()
                    .typeError("size_required")
                    .required("size_required"),
            })
        )
        .min(1, "at_least_one_product"),

});

const generateRFQRef = () =>
    `RFQ-${Date.now().toString().slice(-8)}`;

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '8px',
        minHeight: '44px',
        borderColor: state.isFocused ? '#212121' : '#b0bec5',
        boxShadow: state.isFocused ? '0 0 0 1px #212121' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#212121' : '#b0bec5',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9e9e9e',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 50,
        borderRadius: '8px',
    })
};

export function RFQCreate({ onClose, product_ids = [], selectedVariants = {} }) {

    const dispatch = useDispatch();
    const { state } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation(['rfq', 'common']);
    const { t: ct } = useTranslation("common");

    const { product_color_attributs, product_size_attributs } = useSelector((state) => state.product);

    const { customers, cart_tiems, loading } = useSelector((state) => state.rfq);

    const { currencies } = useSelector((state) => state.currentCurrency);
    const productTypeOptions = [
        { label: t("Requested"), value: "requested" },
        { label: t("Recommended"), value: "recommended" },
    ];

    const {
        control,
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            rfq_reference: generateRFQRef(),
            user_id: null,
            material_submittal: null,
            currency_code: null,
            customer_delivery_address: '',
            latitude: null,
            longitude: null,
            products: [{
                product_id: '',
                quantity: 1,
                attribute_value_color_id: '',
                attribute_value_size_id: ''
            }]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "products",
    });

    useEffect(() => {
        dispatch(fetchColorAttribute());
        dispatch(fetchSizeAttribute());
        dispatch(fetchCustomer());
        dispatch(getCurrencies());

    }, [dispatch]);

    useEffect(() => {
        const products = cart_tiems?.map((product) => {
            return {
                product_id: product?.product_id,
                quantity: 1,
                attribute_value_color_id: selectedVariants[product.product_id]?.color_id
                    ?? product?.varients[0]?.attribute_value_color_id,
                attribute_value_size_id: selectedVariants[product.product_id]?.size_id
                    ?? product?.varients[0]?.attribute_value_size_id
            };
        });

        reset({
            rfq_reference: state?.rfq_reference ? state?.rfq_reference : '',
            rfq_id: state?.rfq_id ? state?.rfq_id : '',
            user_id: state?.customer_id ? state?.customer_id : null,
            currency_code: state?.currency_code ? state?.currency_code : null,
            material_submittal: state?.material_submittal ? state?.material_submittal : null,
            customer_delivery_address: state?.customer_delivery_address ?? '',
            latitude: state?.latitude ?? null,
            longitude: state?.longitude ?? null,
            type: state?.rfq_type,
            products: products
        });

    }, [cart_tiems, selectedVariants]);

    const onSubmit = async (data) => {
        try {
            const payload = { ...data };

            if (payload.rfq_id) {
                delete payload.rfq_reference;
            }
            const result = await dispatch(addRfq(payload)).unwrap();

            if (result.status) {

                if (payload.rfq_id) {
                    Toaster.success(ct("productAddSuccess"));
                } else {
                    Toaster.success(ct("rfqCreateSuccess"));
                }

                dispatch(resetStatus());
                reset();
                onClose();

                navigate("/dashboard/rfqs");

            } else {
                Toaster.error(result.message || ct("somethingWentWrong"));
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || ct("userAddFailed"));
            dispatch(resetStatus());
        }
    };

    /**
     * Handles incrementing or decrementing the product quantity.
     */
    const handleQuantityChange = (index, delta) => {
        const currentQuantity = Number(watch(`products.${index}.quantity`)) || 0;
        const newQuantity = Math.max(1, Math.min(99999, currentQuantity + delta));
        setValue(`products.${index}.quantity`, newQuantity, { shouldValidate: true });
    };

    const currencyOptions =
        currencies?.map((currency) => ({
            value: currency.currency_code,
            label: `${currency.currency_code} - ${currency.currency_name}`,
        })) || [];

    return (
        <Dialog
            open={true}
            handler={onClose}
            size="lg"
            className="rounded-2xl p-0"
        >
            {loading && <ShowLoader />}
            <ScrollToTop />

            {/* HEADER */}
            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-2xl font-bold text-gray-900">{state?.rfq_id ? t('add_items_title') : t('create_new_rfq_title')} </h2>
                {/* <button
                    type="button"
                    onClick={onClose}
                    className="p-1 rounded-full text-gray-500 hover:bg-gray-100 transition"
                >
                    <XMarkIcon className="w-6 h-6" />
                </button> */}
            </DialogHeader>

            <form id="rfq-create-form" onSubmit={handleSubmit(onSubmit)}>

                <DialogBody className="px-6 max-h-[65vh] overflow-y-auto">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <Typography variant="small" color="blue-gray" className="mb-2 font-medium capitalize">
                                {t('rfq_reference_input_label')} <span className="text-red-500">*</span>
                            </Typography>
                            <Input
                                type="text"
                                placeholder={t('rfq_reference_placeholder')}
                                {...register('rfq_reference')}
                                className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                labelProps={{
                                    className: "before:content-none after:content-none",
                                }}
                                disabled={state?.rfq_reference}
                            />
                            {errors.rfq_reference && (
                                <p className="text-red-600 text-sm mt-1">
                                    {t(errors.rfq_reference.message)}
                                </p>
                            )}
                        </div>

                        <div>
                            <Typography variant="small" color="blue-gray" className="mb-2 font-medium capitalize">
                                {t('customer_label')} <span className="text-red-500">*</span>
                            </Typography>
                            <Select
                                placeholder={t('select_customer_placeholder')}
                                options={customers}
                                onChange={(c) => setValue("user_id", c?.value, { shouldValidate: true })}
                                styles={customSelectStyles}
                                isClearable
                                isDisabled={state?.customer_id}
                                noOptionsMessage={() => t('no_customer_available')}
                                value={customers.find(opt => opt.value === watch('user_id')) || null}
                            />
                            {errors.user_id && (
                                <p className="text-red-600 text-sm mt-1">
                                    {t(errors.user_id.message)}
                                </p>
                            )}
                        </div>

                        <div className="md:col-span-2">
                            <Typography variant="small" color="blue-gray" className="mb-2 font-medium capitalize">
                                {t('customer_address_optional_label')}
                            </Typography>
                            <Controller
                                name="customer_delivery_address"
                                control={control}
                                render={({ field }) => (
                                    <DeliveryLocationSearch
                                        value={field.value}
                                        latitude={state?.rfq_id ? undefined : watch("latitude")}
                                        longitude={state?.rfq_id ? undefined : watch("longitude")}
                                        placeholder={t('searchAddressPlaceholder')}
                                        disabled={!!state?.rfq_id}
                                        onChange={(geoData) => {
                                            setValue("customer_delivery_address", geoData.delivery_address || "", { shouldValidate: true });
                                            setValue("latitude", geoData.latitude, { shouldValidate: true });
                                            setValue("longitude", geoData.longitude, { shouldValidate: true });
                                        }}
                                        error={t(errors.customer_delivery_address?.message)}
                                    />
                                )}
                            />
                        </div>

                        {/* Currency Code */}
                        <div className="md:col-span-2">
                            <Typography variant="small" className="font-medium">
                                {t("currencyCodeLabel")} <span className="text-red-500">*</span>
                            </Typography>
                            <Select
                                placeholder={t("currencyCodePlaceholder")}
                                options={currencyOptions}
                                onChange={(selected) => {
                                    setValue("currency_code", selected?.value || "", {
                                        shouldValidate: true,
                                    });
                                }}
                                value={
                                    currencyOptions.find(
                                        (option) => option.value === watch("currency_code")
                                    ) || null
                                }
                                isClearable
                            />
                            <p className="text-red-500 text-sm mt-1">
                                {t(errors.currency_code?.message)}
                            </p>
                        </div>

                        <Controller
                            name="material_submittal"
                            control={control}
                            defaultValue="no"
                            render={({ field }) => (
                                <Checkbox
                                    checked={field.value === "yes" || state?.material_submittal === 'yes'}
                                    onChange={(e) => field.onChange(e.target.checked ? "yes" : "no")}
                                    label={
                                        <Typography
                                            variant="small"
                                            color="gray"
                                            className="flex items-center justify-start font-medium"
                                        >
                                            {t('provide_material_submittal_label')}
                                        </Typography>
                                    }
                                    containerProps={{ className: "-ml-2.5" }}
                                    disabled={state?.material_submittal}
                                />
                            )}
                        />
                    </div>

                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 font-bold capitalize">
                            {t('total_items_count_label', { count: cart_tiems?.length || 0 })}
                        </Typography>

                    </div>

                    {/* Products Header and Add Button */}
                    {/* <div className="flex justify-between items-center pt-4 border-t">
                        <h3 className="text-xl font-bold text-gray-800">Total number of items: {fields.length}</h3>
                        <button
                            type="button"
                            onClick={() =>
                                append({
                                    product_id: defaultProductId, // Default to first available product ID
                                    quantity: 1,
                                    color: defaultColorId,
                                    size: defaultSizeId
                                })
                            }
                            className="flex items-center gap-1 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition"
                        >
                            <PlusIcon className="w-4 h-4" /> Add Item
                        </button>
                    </div> */}

                    <div className="p-5 border rounded-xl shadow-sm mt-4">
                        {/* Header Row */}
                        <div className="flex text-xs font-semibold uppercase text-gray-500 mb-3 border-b pb-2">
                            <div className="flex-1 min-w-0 pr-4">
                                {t('product')}
                            </div>
                            <div className="w-[150px] hidden sm:block shrink-0 px-2 text-center">
                                {t('skuNumber')}
                            </div>
                            <div className="w-[150px] hidden sm:block shrink-0 px-2 text-center">
                                {t('productType')}
                            </div>
                            <div className="w-[155px] shrink-0 px-2 text-center">
                                {t('quantity_label')}
                            </div>
                            <div className="w-[60px] shrink-0 flex justify-end">
                                {t('remove_button')}
                            </div>
                        </div>

                        <div className="space-y-0 mt-0">
                            {cart_tiems?.map((item, index) => {

                                const currentQuantity = watch(`products.${index}.quantity`);

                                let color_id = watch(`products.${index}.attribute_value_color_id`);
                                let size_id = watch(`products.${index}.attribute_value_size_id`);

                                let image =
                                    item?.varients
                                        ?.find((color) => color.color_id === color_id)
                                        ?.sizes?.find((size) => size.size_id === size_id)
                                        ?.product_image || null;


                                return (
                                    <div
                                        key={item.id}
                                        className="flex items-start py-5 border-b border-gray-200 last:border-b-0"
                                    >
                                        <div className="flex-1 min-w-0 flex items-start gap-4 pr-4">
                                            {/* Product Image  */}
                                            <img
                                                src={image ?? item.product_image}
                                                alt={item.product_name}
                                                className="w-20 h-20 object-cover border border-gray-100 rounded-lg shrink-0"
                                            />

                                            <div className="flex flex-col min-w-0 w-full">

                                                {/* STATIC PRODUCT NAME */}
                                                <Typography variant="h6" className="text-base font-semibold text-gray-800 truncate mb-1">
                                                    {item.product_name}
                                                </Typography>

                                                {/* Category (Displayed for context) */}
                                                <Typography variant="small" className="text-sm text-gray-500 mb-3">
                                                    {t('category')}: {item.category}
                                                </Typography>

                                                <ProductVariantSelector
                                                    variants={item?.varients}
                                                    defaultColorId={selectedVariants[item.product_id]?.color_id}
                                                    defaultSizeId={selectedVariants[item.product_id]?.size_id}
                                                    onColorChange={(colorId) => setValue(`products.${index}.attribute_value_color_id`, colorId, { shouldValidate: true })}
                                                    onSizeChange={(sizeId) => setValue(`products.${index}.attribute_value_size_id`, sizeId, { shouldValidate: true })}
                                                />

                                            </div>
                                        </div>

                                        {/* 2. SKU Number */}
                                        <div className="w-[110px] hidden sm:block shrink-0 px-2 flex justify-center">
                                            <Typography variant="small" className="font-medium text-gray-700 mt-2 break-all">
                                                {item.sku_number}
                                            </Typography>
                                        </div>
                                        {/* 3. Product Type */}
                                        <div className="w-[110px] sm:w-[150px] hidden sm:block shrink-0 px-2 flex justify-center items-center">
                                            <Select
                                                options={productTypeOptions}
                                                value={productTypeOptions.find(
                                                    (opt) => opt.value === watch(`products.${index}.product_type`)
                                                ) || productTypeOptions[0]}
                                                onChange={(option) => {
                                                    setValue(`products.${index}.product_type`, option.value);
                                                }}
                                            />
                                        </div>


                                        {/* 3. Quantity Input */}
                                        <div className="w-[155px] shrink-0 px-2 flex justify-center relative">
                                            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                                                <button
                                                    type="button"
                                                    onClick={() => handleQuantityChange(index, -1)}
                                                    className="p-1 text-gray-700 hover:bg-gray-100 focus:outline-none disabled:opacity-50"
                                                    disabled={Number(currentQuantity) <= 1}
                                                >
                                                    <MinusIcon className="h-4 w-4" />
                                                </button>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={99999}
                                                    value={currentQuantity ?? ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val === '') {
                                                            setValue(`products.${index}.quantity`, '', { shouldValidate: true });
                                                        } else {
                                                            const num = parseInt(val, 10);
                                                            if (!Number.isNaN(num)) {
                                                                if (num > 99999) {
                                                                    setValue(`products.${index}.quantity`, 99999, { shouldValidate: true });
                                                                } else {
                                                                    setValue(`products.${index}.quantity`, num, { shouldValidate: true });
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const val = parseInt(e.target.value, 10);
                                                        if (!val || val < 1) {
                                                            setValue(`products.${index}.quantity`, 1, { shouldValidate: true });
                                                        } else if (val > 99999) {
                                                            setValue(`products.${index}.quantity`, 99999, { shouldValidate: true });
                                                        }
                                                    }}
                                                    onWheel={(e) => e.target.blur()}
                                                    className="text-sm px-1 py-1 w-20 text-center font-semibold border-x border-gray-300 outline-none appearance-none"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleQuantityChange(index, 1)}
                                                    className="p-1 text-gray-700 hover:bg-gray-100 focus:outline-none disabled:opacity-50"
                                                    disabled={Number(currentQuantity) >= 99999}
                                                >
                                                    <PlusIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                            {errors.products?.[index]?.quantity && (
                                                <p className="text-red-600 text-xs mt-1 absolute bottom-[-18px] whitespace-nowrap">
                                                    {t(errors.products[index].quantity.message)}
                                                </p>
                                            )}
                                        </div>


                                        {/* 4. Remove Button */}
                                        <div className="w-[60px] shrink-0 flex justify-end px-2">
                                            <button
                                                type="button"
                                                onClick={() => remove(index)}
                                                className="text-red-500 hover:text-red-700 flex items-center transition text-sm font-medium pt-2"
                                                disabled={cart_tiems.length === 1} // Disable remove if only one item remains
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </DialogBody>

                {/* FOOTER */}
                <DialogFooter className="px-6 border-t mt-4 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-50 transition"
                    >
                        {ct('cancel')}
                    </button>
                    <button
                        type="submit"
                        form="rfq-create-form"
                        className="px-6 py-2.5 rounded-lg bg-yellow-600 hover:bg-yellow-700 text-black font-semibold transition"
                    >
                        {state?.rfq_id ? t('add_items_title') : t('create_new_rfq_title')}
                    </button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default RFQCreate;
